import React, { memo, useCallback, useEffect, useRef } from 'react';

import {
  type CampaignSource,
  REBATE_CAMPAIGN_TYPE,
  useCampaignCardScale,
  useCampaignCardStyles,
  useCampaignCollapsibleState,
  useCampaignDataAttributes,
  useHasMany,
  usePanelInfo,
} from 'features/campaigns';
import { useTrueMobile } from 'shared/hooks/useTrueMobile';
import CarouselControls from './components/CarouselControls';
import CarouselHeader from './components/CarouselHeader';
import CarouselItem from './components/CarouselItem';
import { useCampaignsCarousel } from './useCampaignsCarousel';

import campaignsCarouselStyles from './css/CampaignsCarousel.module.css';

interface ICampaignsCarouselProps {
  campaigns: CampaignSource[];
  onCampaignSelect?: (campaign: CampaignSource) => void;
}

const NEIGHBOR_DISTANCE = 1;

const CampaignsCarousel: React.FC<ICampaignsCarouselProps> = ({ campaigns, onCampaignSelect }) => {
  // Section type is read from context by each hook below — nothing to thread.
  const { getCampaignCardStyles } = useCampaignCardStyles();
  const campaignCardStyles = getCampaignCardStyles();
  const dataAttributes = useCampaignDataAttributes({});

  const { carouselRef, selectedCampaignId, handleMouseEnter, handleMouseLeave, handleCampaignSelect, registerCarouselItem } =
    useCampaignsCarousel({ campaigns, onCampaignSelect });

  const selectedIndex = selectedCampaignId ? campaigns.findIndex((c) => c.id === selectedCampaignId) : -1;

  const { isCollapsed, arrow, handleArrowClick } = useCampaignCollapsibleState();
  const hasMany = useHasMany();
  const isMobile = useTrueMobile();

  const panelInfo = usePanelInfo();

  const { titleRef, scale } = useCampaignCardScale(panelInfo.headerTitle || '', {
    maxWidth: isMobile ? 200 : 300,
  });

  const wrapperRef = useRef<HTMLDivElement>(null);
  const prevCollapsedRef = useRef(isCollapsed);

  useEffect(() => {
    if (prevCollapsedRef.current === isCollapsed) return;
    prevCollapsedRef.current = isCollapsed;

    if (!isCollapsed) {
      wrapperRef.current?.setAttribute('data-stretch', 'true');
    }
  }, [isCollapsed]);

  const handleWrapperAnimationEnd = useCallback(() => {
    wrapperRef.current?.removeAttribute('data-stretch');
  }, []);

  return (
    <div
      className={campaignsCarouselStyles.carouselContainer}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {!isCollapsed && panelInfo.type !== REBATE_CAMPAIGN_TYPE ? (
        <CarouselHeader hasMany={hasMany} panelInfo={panelInfo} titleRef={titleRef} scale={scale} />
      ) : null}

      <div className={campaignsCarouselStyles.carouselHeader}></div>

      <div ref={wrapperRef} className={campaignsCarouselStyles.carouselWrapper} onAnimationEnd={handleWrapperAnimationEnd}>
        <div
          ref={carouselRef}
          className={campaignsCarouselStyles.carouselScroll}
          data-campaign-count={campaigns.length}
          {...dataAttributes}
        >
          {campaigns.map((campaign, index) => {
            const isActive = selectedCampaignId === campaign.id;
            let side;

            if(!isActive && selectedIndex !== -1) {
              if(Math.abs(selectedIndex - index) === NEIGHBOR_DISTANCE) {
                if(index < selectedIndex) {
                  side = 'left';
                } else {
                  side = 'right';
                }
              } else {
                side = 'distant';
              }
            } else {
              side = undefined;
            }

            return (
              <CarouselItem
                key={`${campaign.id}-item`}
                campaign={campaign}
                isActive={isActive}
                side={side}
                onSelect={handleCampaignSelect}
                dataAttributes={dataAttributes}
                registerRef={registerCarouselItem}
              />
            );
          })}
        </div>
      </div>

      <CarouselControls
        arrow={arrow}
        isCollapsed={isCollapsed}
        onArrowClick={handleArrowClick}
        hasMany={hasMany}
        campaignCardStyles={campaignCardStyles}
      />
    </div>
  );
};

export default memo(CampaignsCarousel);
